/**
 * Blog Posts Paginated — SEO-optimized, URL-addressable article grid.
 *
 * Architecture:
 *   Phase 1 (instant): Read ?page=N, fetch exactly 1 page of articles, hide sections above if page > 1.
 *   Phase 2 (background): Build a cursor map (total count + page→cursor lookup) for random-access pagination.
 *
 * Config is injected per-section via window.__blogPaginatedConfig[sectionId].
 */

// ============================================================
// URL State
// ============================================================

const URLState = {
  getPage() {
    const params = new URLSearchParams(window.location.search);
    return Math.max(1, parseInt(params.get('page'), 10) || 1);
  },

  setPage(page, replace = false) {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    const method = replace ? 'replaceState' : 'pushState';
    history[method]({ page }, '', url.toString());
  },
};

// ============================================================
// CursorMap — background count + random-access page lookup
// ============================================================

class CursorMap {
  constructor({ articlesPerPage, firstPageCount, fetchCursorsFn }) {
    this.articlesPerPage = articlesPerPage;
    this.firstPageCount = firstPageCount || articlesPerPage;
    this.fetchCursorsFn = fetchCursorsFn;
    this.map = new Map(); // pageNumber → afterCursor
    this.totalArticles = 0;
    this.totalPages = 0;
    this.isComplete = false;
    this._buildPromise = null;
    this._cacheKey = null;
  }

  setCacheKey(key) {
    this._cacheKey = 'blogPagCursors_' + key;
  }

  async build() {
    if (this._buildPromise) return this._buildPromise;

    // Try sessionStorage cache first
    if (this._cacheKey) {
      try {
        const cached = sessionStorage.getItem(this._cacheKey);
        if (cached) {
          const data = JSON.parse(cached);
          this.map = new Map(data.map);
          this.totalArticles = data.totalArticles;
          this.totalPages = data.totalPages;
          this.isComplete = true;
          this._buildPromise = Promise.resolve();
          return this._buildPromise;
        }
      } catch (_) {
        // sessionStorage unavailable or corrupted — rebuild
      }
    }

    this._buildPromise = this._doBuild();
    return this._buildPromise;
  }

  async _doBuild() {
    // Page 1 always starts from cursor null
    this.map.set(1, null);

    let afterCursor = null;
    let totalCount = 0;
    let hasNext = true;

    while (hasNext) {
      const { edges, pageInfo } = await this.fetchCursorsFn(afterCursor);
      const batchSize = edges.length;

      for (let i = 0; i < batchSize; i++) {
        const globalIndex = totalCount + i;
        // Page 1 may show fewer articles than subsequent pages (firstPageCount).
        // Boundary after page 1: globalIndex === firstPageCount - 1
        // Boundaries after pages 2+: globalIndex === firstPageCount + n * articlesPerPage - 1
        let nextPageNumber = null;
        if (globalIndex + 1 === this.firstPageCount) {
          nextPageNumber = 2;
        } else if (globalIndex >= this.firstPageCount) {
          const offsetIndex = globalIndex - this.firstPageCount; // 0-based within pages 2+
          if ((offsetIndex + 1) % this.articlesPerPage === 0) {
            nextPageNumber = 2 + Math.floor((offsetIndex + 1) / this.articlesPerPage);
          }
        }
        if (nextPageNumber !== null) {
          this.map.set(nextPageNumber, edges[i].cursor);
        }
      }

      totalCount += batchSize;
      afterCursor = pageInfo.endCursor;
      hasNext = pageInfo.hasNextPage;
    }

    this.totalArticles = totalCount;
    if (totalCount <= this.firstPageCount) {
      this.totalPages = totalCount > 0 ? 1 : 0;
    } else {
      this.totalPages = 1 + Math.ceil((totalCount - this.firstPageCount) / this.articlesPerPage);
    }
    this.isComplete = true;

    // Persist to sessionStorage
    if (this._cacheKey) {
      try {
        sessionStorage.setItem(
          this._cacheKey,
          JSON.stringify({
            map: Array.from(this.map.entries()),
            totalArticles: this.totalArticles,
            totalPages: this.totalPages,
          })
        );
      } catch (_) {
        // quota exceeded or unavailable — non-critical
      }
    }
  }

  getCursorForPage(page) {
    if (page <= 1) return null;
    return this.map.get(page) ?? null;
  }
}

// ============================================================
// GraphQL helpers
// ============================================================

function buildGraphQLEndpoint(storeDomain, apiVersion) {
  return `https://${storeDomain}/api/${apiVersion}/graphql.json`;
}

const ARTICLES_QUERY = `
  query PaginatedArticlesByBlogs(
    $first: Int!,
    $after: String,
    $sortKey: ArticleSortKeys = PUBLISHED_AT,
    $reverse: Boolean = true,
    $query: String
  ) {
    articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
      edges {
        cursor
        node {
          id
          title
          handle
          excerpt
          content
          onlineStoreUrl
          blog { title, handle }
          authorV2 { name }
          image { altText, url, width, height }
          publishedAt
        }
      }
      pageInfo { hasNextPage, hasPreviousPage, startCursor, endCursor }
    }
  }
`;

const COUNT_QUERY = `
  query CountArticles($first: Int!, $after: String, $sortKey: ArticleSortKeys = PUBLISHED_AT, $reverse: Boolean = true, $query: String) {
    articles(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, query: $query) {
      edges { cursor }
      pageInfo { hasNextPage, endCursor }
    }
  }
`;

function buildBlogQuery(blogTitles) {
  if (!blogTitles || blogTitles.length === 0) return undefined;
  return blogTitles.map((t) => `blog_title:${JSON.stringify(t)}`).join(' OR ');
}

async function fetchArticlesBatch(endpoint, { first, after = null, sortOrder = 'most_recent', blogTitles = [], excerptWordCount = 20, signal }) {
  const variables = {
    first,
    after,
    sortKey: 'PUBLISHED_AT',
    reverse: sortOrder === 'most_recent',
    query: buildBlogQuery(blogTitles),
  };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: ARTICLES_QUERY, variables }),
    signal,
  });

  const result = await response.json();

  if (!response.ok || result.errors) {
    console.error('GraphQL error:', { status: response.status, errors: result.errors });
    throw new Error('Failed to fetch articles batch');
  }

  const connection = result.data.articles;

  const articles = connection.edges.map((edge) => {
    const node = edge.node;
    return {
      cursor: edge.cursor,
      id: node.id,
      title: node.title,
      handle: node.handle,
      excerpt: buildExcerpt({ excerpt: node.excerpt, content: node.content }, excerptWordCount),
      url: node.onlineStoreUrl,
      blog_title: node.blog ? node.blog.title : null,
      blog_handle: node.blog ? node.blog.handle : null,
      author_name: node.authorV2 ? node.authorV2.name : null,
      image_url: node.image ? node.image.url : null,
      image_alt: node.image ? node.image.altText : null,
      image_width: node.image ? node.image.width : null,
      image_height: node.image ? node.image.height : null,
      published_at: node.publishedAt,
      reading_minutes: estimateReadingTime(node.content),
    };
  });

  return { articles, pageInfo: connection.pageInfo };
}

async function fetchCursorsOnly(endpoint, { after = null, sortOrder = 'most_recent', blogTitles = [] }) {
  const variables = {
    first: 250,
    after,
    sortKey: 'PUBLISHED_AT',
    reverse: sortOrder === 'most_recent',
    query: buildBlogQuery(blogTitles),
  };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: COUNT_QUERY, variables }),
  });

  const result = await response.json();

  if (!response.ok || result.errors) {
    throw new Error('Failed to fetch article cursors');
  }

  return {
    edges: result.data.articles.edges,
    pageInfo: result.data.articles.pageInfo,
  };
}

async function resolveBlogHandles(endpoint, handles) {
  if (!handles.length) return [];

  const aliases = handles.map((h, i) => `blog${i}: blog(handle: ${JSON.stringify(h)}) { title }`).join('\n');
  const query = `query ResolveBlogHandles { ${aliases} }`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) throw new Error('Failed to resolve blog handles');
  const result = await response.json();

  return handles.map((_, i) => result.data?.[`blog${i}`]?.title ?? null).filter(Boolean);
}

// ============================================================
// Text helpers
// ============================================================

function buildExcerpt({ excerpt, content }, wordCount) {
  if (excerpt && excerpt.trim().length > 0) {
    return truncateWords(excerpt, wordCount);
  }
  if (!content || content.trim().length === 0) return '';

  const tmp = document.createElement('div');
  tmp.innerHTML = content;
  const text = (tmp.textContent || tmp.innerText || '').trim();
  if (!text) return '';

  return truncateWords(text, wordCount);
}

function estimateReadingTime(content) {
  if (!content || content.trim().length === 0) return null;

  const tmp = document.createElement('div');
  tmp.innerHTML = content;
  const text = (tmp.textContent || tmp.innerText || '').trim();
  if (!text) return null;

  const wordCount = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / 250));
}

function truncateWords(text, count) {
  const words = text.split(/\s+/);
  if (words.length <= count) return text;
  return words.slice(0, count).join(' ') + '...';
}

function shopifyImageUrl(url, width) {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}width=${width}`;
}

// ============================================================
// Rendering — Article cards
// ============================================================

const SRCSET_WIDTHS = [320, 480, 640, 750, 900, 1100, 1500];
const ARTICLE_TITLE_WORD_COUNT = 8;

function renderArticleCards(containerEl, articles, config) {
  containerEl.innerHTML = '';

  if (articles.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'article-cards__empty';
    empty.textContent = 'No articles found.';
    containerEl.appendChild(empty);
    return;
  }

  articles.forEach((article) => {
    const card = document.createElement('div');
    card.className = 'article-card';
    card.setAttribute('role', 'listitem');

    const link = document.createElement('a');
    link.className = 'article-card__link full-unstyled-link';
    link.href = article.url || '#';

    if (article.image_url) {
      const imgWrapper = document.createElement('div');
      imgWrapper.className = 'article-card__image-wrapper';

      const img = document.createElement('img');
      img.className = 'article-card__image';
      img.src = shopifyImageUrl(article.image_url, 640);
      img.srcset = SRCSET_WIDTHS.map((w) => `${shopifyImageUrl(article.image_url, w)} ${w}w`).join(', ');
      img.sizes = `(max-width: 749px) calc(100vw / ${config.columnsMobile || 1}), calc(100vw / ${config.gridColumns || 3})`;
      img.alt = article.image_alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (article.image_width) img.width = article.image_width;
      if (article.image_height) img.height = article.image_height;

      imgWrapper.appendChild(img);
      link.appendChild(imgWrapper);
    }

    if (config.showBlogTitle && article.blog_title) {
      const blogTitleEl = document.createElement('div');
      blogTitleEl.className = 'article-card__blog-title';
      blogTitleEl.textContent = article.blog_title;
      link.appendChild(blogTitleEl);
    }

    if (config.showReadTime && article.reading_minutes) {
      const readEl = document.createElement('div');
      readEl.className = 'article-card__reading-time time-read';
      readEl.textContent = article.reading_minutes + ' min read';
      link.appendChild(readEl);
    }

    const titleEl = document.createElement('h3');
    titleEl.className = 'article-card__article-title';
    titleEl.textContent = truncateWords(article.title || '', ARTICLE_TITLE_WORD_COUNT);
    link.appendChild(titleEl);

    if (config.showExcerpt && article.excerpt) {
      const excerptEl = document.createElement('div');
      excerptEl.className = 'article-card__excerpt';
      excerptEl.textContent = article.excerpt;
      link.appendChild(excerptEl);
    }

    if (config.showAuthor && article.author_name) {
      const authorEl = document.createElement('div');
      authorEl.className = 'article-card__author time-read';
      authorEl.textContent = article.author_name;
      link.appendChild(authorEl);
    }

    card.appendChild(link);
    containerEl.appendChild(card);
  });
}

// ============================================================
// Rendering — Pagination
// ============================================================

const PAGE_RANGE_LIMIT = 10;

function buildPageRange(current, total) {
  if (!total || total <= 1) return [];

  // If total fits within LIMIT + 1, show every page with no ellipsis
  if (total <= PAGE_RANGE_LIMIT + 1) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  // Determine the window block the current page falls in (0-indexed)
  // e.g. LIMIT=10: pages 1-10 → block 0, pages 11-20 → block 1, etc.
  const blockIndex  = Math.floor((current - 1) / PAGE_RANGE_LIMIT);
  const windowStart = blockIndex * PAGE_RANGE_LIMIT + 1;
  const windowEnd   = Math.min(windowStart + PAGE_RANGE_LIMIT - 1, total);

  const pages = [];

  // Leading anchor: show page 1 + ellipsis when the window doesn't start at 1
  if (windowStart > 1) {
    pages.push(1);
    pages.push('...');
  }

  // The visible window
  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i);
  }

  // Trailing: if exactly one page remains after the window, append it without
  // an ellipsis; if more remain, append ellipsis + last page
  if (windowEnd === total - 1) {
    pages.push(total);
  } else if (windowEnd < total - 1) {
    pages.push('...');
    pages.push(total);
  }

  return pages;
}

function renderPagination(navEl, { currentPage, totalPages, onPageChange }) {
  const numbersContainer = navEl.querySelector('[data-pagination-numbers]');
  const prevLink = navEl.querySelector('[data-pagination-prev]');
  const nextLink = navEl.querySelector('[data-pagination-next]');

  if (!numbersContainer || !prevLink || !nextLink) return;

  numbersContainer.innerHTML = '';

  const basePath = window.location.pathname;
  const pages = buildPageRange(currentPage, totalPages);

  pages.forEach((item) => {
    if (item === '...') {
      const span = document.createElement('span');
      span.className = 'pagination__ellipsis blog__button';
      span.textContent = '\u2026';
      span.setAttribute('aria-hidden', 'true');
      numbersContainer.appendChild(span);
    } else {
      const a = document.createElement('a');
      a.className = 'blog__button full-unstyled-link';
      a.href = item === 1 ? basePath : `${basePath}?page=${item}`;
      a.textContent = String(item);
      a.setAttribute('aria-label', `Page ${item}`);
      if (item === currentPage) {
        a.setAttribute('aria-current', 'page');
      }
      a.addEventListener('click', (e) => {
        e.preventDefault();
        onPageChange(item);
      });
      numbersContainer.appendChild(a);
    }
  });

  // Previous
  if (currentPage > 1) {
    prevLink.href = currentPage === 2 ? basePath : `${basePath}?page=${currentPage - 1}`;
    prevLink.style.display = '';
    prevLink.onclick = (e) => {
      e.preventDefault();
      onPageChange(currentPage - 1);
    };
  } else {
    prevLink.style.display = 'none';
    prevLink.onclick = null;
  }

  // Next
  if (totalPages && currentPage < totalPages) {
    nextLink.href = `${basePath}?page=${currentPage + 1}`;
    nextLink.style.display = '';
    nextLink.onclick = (e) => {
      e.preventDefault();
      onPageChange(currentPage + 1);
    };
  } else if (!totalPages) {
    // Total unknown yet — show next optimistically
    nextLink.href = `${basePath}?page=${currentPage + 1}`;
    nextLink.style.display = '';
    nextLink.onclick = (e) => {
      e.preventDefault();
      onPageChange(currentPage + 1);
    };
  } else {
    nextLink.style.display = 'none';
    nextLink.onclick = null;
  }
}

// ============================================================
// Hide / Show sections above
// ============================================================

function hideSectionsAbove(sectionEl) {
  const wrapper = sectionEl.closest('.shopify-section');
  if (!wrapper) return;

  let sibling = wrapper.previousElementSibling;
  while (sibling) {
    if (
      sibling.classList.contains('shopify-section') &&
      !sibling.classList.contains('shopify-section-group-header-group') &&
      !sibling.classList.contains('shopify-section-group-footer-group')
    ) {
      sibling.style.display = 'none';
      sibling.setAttribute('data-hidden-by-pagination', 'true');
    }
    sibling = sibling.previousElementSibling;
  }
}

function showSectionsAbove() {
  document.querySelectorAll('[data-hidden-by-pagination="true"]').forEach((el) => {
    el.style.display = '';
    el.removeAttribute('data-hidden-by-pagination');
  });
}

// ============================================================
// SEO meta — dynamic <link rel="next/prev"> and title
// ============================================================

function updateSeoMeta(currentPage, totalPages) {
  document.querySelectorAll('link[data-blog-pagination]').forEach((el) => el.remove());

  const base = window.location.pathname;

  if (currentPage > 1) {
    const prev = document.createElement('link');
    prev.rel = 'prev';
    prev.href = currentPage === 2 ? base : `${base}?page=${currentPage - 1}`;
    prev.setAttribute('data-blog-pagination', 'true');
    document.head.appendChild(prev);
  }

  if (totalPages && currentPage < totalPages) {
    const next = document.createElement('link');
    next.rel = 'next';
    next.href = `${base}?page=${currentPage + 1}`;
    next.setAttribute('data-blog-pagination', 'true');
    document.head.appendChild(next);
  }

  // Update <title> to include page suffix (mirrors theme.liquid pattern)
  const baseTitle = document.title.replace(/\s*\u2013\s*Page\s+\d+/g, '');
  if (currentPage > 1) {
    document.title = baseTitle.replace(/(\s*\u2013\s*[^–]+)$/, ` \u2013 Page ${currentPage}$1`);
  } else {
    document.title = baseTitle;
  }
}

// ============================================================
// Main section initializer
// ============================================================

document.addEventListener('DOMContentLoaded', () => {
  const configs = window.__blogPaginatedConfig || {};
  for (const [sectionId, config] of Object.entries(configs)) {
    initSection(sectionId, config);
  }
});

async function initSection(sectionId, config) {
  const sectionEl = document.getElementById('blog-articles-' + sectionId);
  if (!sectionEl) return;

  const containerEl = sectionEl.querySelector('[data-article-cards]');
  const paginationEl = sectionEl.querySelector('[data-pagination]');

  if (!containerEl || !paginationEl) return;

  const endpoint = buildGraphQLEndpoint(config.storeDomain, config.apiVersion);
  const articlesPerPage = config.gridColumns * config.gridRows;
  const firstRowCount = config.gridColumns;
  const currentPage = URLState.getPage();

  // Immediately hide sections above on page > 1 (before any network requests)
  if (currentPage > 1) {
    hideSectionsAbove(sectionEl);
  }

  // AbortController for fetch cancellation
  let activeController = null;

  // Resolve blog handles → titles
  let blogTitles = [];
  const handles = (config.blogHandles || []).filter((h) => h.trim().length > 0);
  if (handles.length > 0) {
    try {
      blogTitles = await resolveBlogHandles(endpoint, handles);
    } catch (err) {
      console.error('Failed to resolve blog handles:', err);
    }
  }

  // Build cursor map in background
  const firstPageCount = config.firstRowOnly ? firstRowCount : articlesPerPage;
  const cursorMap = new CursorMap({
    articlesPerPage,
    firstPageCount,
    fetchCursorsFn: (afterCursor) =>
      fetchCursorsOnly(endpoint, { after: afterCursor, sortOrder: config.sortOrder, blogTitles }),
  });
  cursorMap.setCacheKey(`${handles.join(',')}_${config.sortOrder}_${articlesPerPage}_p1:${firstPageCount}`);
  const cursorMapPromise = cursorMap.build();

  // Navigation handler
  async function navigateToPage(page, { pushState: doPush = true } = {}) {
    // Cancel in-flight fetch
    if (activeController) activeController.abort();
    activeController = new AbortController();

    // Clamp page
    if (cursorMap.isComplete && page > cursorMap.totalPages && cursorMap.totalPages > 0) {
      page = cursorMap.totalPages;
    }
    if (page < 1) page = 1;

    // Update URL
    if (doPush) URLState.setPage(page);

    // Hide/show sections above
    if (page > 1) {
      hideSectionsAbove(sectionEl);
    } else {
      showSectionsAbove();
    }

    // Loading state
    containerEl.setAttribute('aria-busy', 'true');

    try {
      // For page > 1, we need the cursor map
      let afterCursor = null;
      if (page > 1) {
        if (!cursorMap.isComplete) await cursorMapPromise;
        afterCursor = cursorMap.getCursorForPage(page);

        // Re-clamp after cursor map is built
        if (page > cursorMap.totalPages && cursorMap.totalPages > 0) {
          page = cursorMap.totalPages;
          URLState.setPage(page, true);
          afterCursor = cursorMap.getCursorForPage(page);
        }
      }

      const fetchCount = (config.firstRowOnly && page === 1) ? firstRowCount : articlesPerPage;
      const { articles } = await fetchArticlesBatch(endpoint, {
        first: fetchCount,
        after: afterCursor,
        sortOrder: config.sortOrder,
        blogTitles,
        excerptWordCount: config.excerptWordCount,
        signal: activeController.signal,
      });

      renderArticleCards(containerEl, articles, config);

      // Update pagination
      renderPagination(paginationEl, {
        currentPage: page,
        totalPages: cursorMap.isComplete ? cursorMap.totalPages : null,
        onPageChange: (p) => navigateToPage(p),
      });

      // SEO meta
      updateSeoMeta(page, cursorMap.isComplete ? cursorMap.totalPages : null);

      // Hide pagination if no articles
      paginationEl.style.display = articles.length === 0 ? 'none' : '';
    } catch (err) {
      if (err.name === 'AbortError') return; // navigation was superseded
      console.error('Error loading articles page:', err);
    } finally {
      containerEl.setAttribute('aria-busy', 'false');
    }

    // Scroll and focus (only on user-initiated navigation, not initial load)
    if (doPush) {
      sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      containerEl.focus({ preventScroll: true });
    }
  }

  // Load initial page
  await navigateToPage(currentPage, { pushState: false });

  // Once cursor map finishes, re-render pagination with totals
  if (!cursorMap.isComplete) {
    cursorMapPromise.then(() => {
      const page = URLState.getPage();

      // Re-clamp if out of range
      if (page > cursorMap.totalPages && cursorMap.totalPages > 0) {
        navigateToPage(cursorMap.totalPages, { pushState: false });
        URLState.setPage(cursorMap.totalPages, true);
        return;
      }

      renderPagination(paginationEl, {
        currentPage: page,
        totalPages: cursorMap.totalPages,
        onPageChange: (p) => navigateToPage(p),
      });
      updateSeoMeta(page, cursorMap.totalPages);
    });
  }

  // Browser back/forward
  window.addEventListener('popstate', (event) => {
    const page = event.state?.page || URLState.getPage();
    navigateToPage(page, { pushState: false });
  });
}
